import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/client";

/**
 * Review and run an AI proposal without leaving the screen you're on.
 *
 * "Remediate (AI)" used to POST, say "review it in AI Actions", and leave you
 * to navigate away, find the proposal in a queue, and approve it there. You
 * were on the alert; the whole point of the button was to deal with the alert.
 *
 * So this shows what the AI wants to do, why, and on what — and approves and
 * runs it in place, then reports the outcome without another trip.
 *
 * Reusable on purpose: alerts today, tickets and the device page next, so
 * there is one review surface rather than one per screen.
 */

export type AIAction = {
  id: number;
  action_type: string;
  params: Record<string, unknown>;
  risk_tier: "low" | "medium" | "high";
  status: string;
  proposed_reason: string | null;
  device_hostname: string | null;
  customer_name: string | null;
  instruction: string | null;
  /** What this will actually do to somebody's day. Computed server-side. */
  consequence: string | null;
  disruptive: boolean;
  result: Record<string, unknown> | null;
  error_message: string | null;
  agent_command_id: number | null;
  executed_at: string | null;
};

const RISK_STYLE: Record<string, string> = {
  low: "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300",
  medium: "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
  high: "bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300",
};

/** Turn "restart_service" into "Restart service". */
function humanise(actionType: string) {
  const s = (actionType || "").replace(/_/g, " ").trim();
  return s ? s[0].toUpperCase() + s.slice(1) : "Action";
}

export default function AIActionReview({
  actionId,
  onClose,
  onDone,
  // "AI remediation" is right on an alert and wrong on a drafted ticket reply,
  // which is a proposal but not a repair.
  title = "AI proposal",
}: {
  actionId: number;
  onClose: () => void;
  title?: string;
  /** Fired after a successful run so the parent can refresh its own list. */
  onDone?: () => void;
}) {
  const [action, setAction] = useState<AIAction | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "run" | "reject">(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const pollRef = useRef<number | null>(null);

  const load = useCallback(
    () =>
      api
        .get(`/api/v1/ai/actions/${actionId}`)
        .then((r) => setAction(r.data))
        .catch((e) => setError(e?.response?.data?.detail || "Could not load the proposal")),
    [actionId],
  );

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  // Execution is asynchronous once it reaches the agent, so the outcome lands
  // after the request returns. Poll briefly rather than making the person
  // refresh — going to look for the result elsewhere is the thing this
  // component exists to avoid.
  useEffect(() => {
    if (!action || !["approved", "executing", "running"].includes(action.status)) return;
    pollRef.current = window.setInterval(load, 3000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [action, load]);

  const settled = action && ["executed", "failed", "rejected"].includes(action.status);

  const run = async () => {
    if (!action) return;
    // Same gate the server enforces: a HIGH-risk action needs a reason on the
    // record. Checking here too means the person is told before the round trip.
    if (action.risk_tier === "high" && !note.trim()) {
      setError("A high-risk action needs an approval note.");
      return;
    }
    setBusy("run");
    setError("");
    try {
      // The single combined endpoint, not approve-then-execute: two calls can
      // leave an action approved but not run, with an error message that does
      // not say which half failed.
      const r = await api.post(`/api/v1/ai/actions/${action.id}/approve-and-execute`, {
        note: note.trim() || undefined,
      });
      setAction(r.data);
      onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not run it");
      load();
    } finally {
      setBusy(null);
    }
  };

  const reject = async () => {
    if (!action) return;
    setBusy("reject");
    setError("");
    try {
      const r = await api.post(`/api/v1/ai/actions/${action.id}/reject`, {
        note: note.trim() || undefined,
      });
      setAction(r.data);
      onDone?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Could not reject it");
    } finally {
      setBusy(null);
    }
  };

  const paramEntries = Object.entries(action?.params || {}).filter(
    ([k]) => k !== "alert_id" && k !== "ticket_id",
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <span className="text-lg">✨</span>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex-1">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="p-5 space-y-3">
          {loading ? (
            <p className="text-sm text-gray-400">Reading the proposal…</p>
          ) : !action ? (
            <p className="text-sm text-red-600 dark:text-red-400">{error || "Not found."}</p>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {humanise(action.action_type)}
                </span>
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                    RISK_STYLE[action.risk_tier] || "bg-gray-100 dark:bg-gray-700"
                  }`}
                >
                  {action.risk_tier} risk
                </span>
                {action.status !== "proposed" && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {action.status}
                  </span>
                )}
              </div>

              {(action.device_hostname || action.customer_name) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {[action.customer_name, action.device_hostname].filter(Boolean).join(" · ")}
                </p>
              )}

              {/* The consequence, before the rationale. A risk tier says how
                  worried to be without saying what to be worried about, and
                  the person about to click Approve needs the second one. */}
              {action.consequence && !settled && (
                <div
                  className={`rounded-lg border p-3 flex gap-2 ${
                    action.disruptive
                      ? "border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/25"
                      : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40"
                  }`}
                >
                  {action.disruptive && <span className="text-base shrink-0">⚠️</span>}
                  <div>
                    {action.disruptive && (
                      <div className="text-[11px] uppercase tracking-wide font-semibold text-amber-800 dark:text-amber-300 mb-0.5">
                        This may break something
                      </div>
                    )}
                    <p className="text-sm text-gray-800 dark:text-gray-200">{action.consequence}</p>
                  </div>
                </div>
              )}

              {action.instruction && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Asked for: “{action.instruction}”
                </p>
              )}

              {action.proposed_reason && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-700 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                    Why
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{action.proposed_reason}</p>
                </div>
              )}

              {paramEntries.length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                    What it will do
                  </div>
                  <dl className="text-xs space-y-1.5">
                    {paramEntries.map(([k, v]) => {
                      const text = typeof v === "object" ? JSON.stringify(v, null, 1) : String(v);
                      // A drafted reply is a paragraph, not a value. Showing it
                      // as inline break-all monospace made the one thing you
                      // are being asked to read the hardest thing to read.
                      const isProse = text.length > 120 || text.includes("\n");
                      return isProse ? (
                        <div key={k}>
                          <dt className="text-gray-500 dark:text-gray-400 mb-0.5">
                            {k.replace(/_/g, " ")}
                          </dt>
                          <dd className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 p-2 max-h-56 overflow-y-auto">
                            {text}
                          </dd>
                        </div>
                      ) : (
                        <div key={k} className="flex gap-2">
                          <dt className="text-gray-500 dark:text-gray-400 min-w-[7rem]">
                            {k.replace(/_/g, " ")}
                          </dt>
                          <dd className="text-gray-800 dark:text-gray-200 font-mono break-all">
                            {text}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              )}

              {/* Outcome, in place. The point of the panel. */}
              {action.error_message && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/25 border border-red-200 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300">
                  {action.error_message}
                </div>
              )}
              {action.status === "executed" && !action.error_message && (
                <div className="rounded-lg bg-green-50 dark:bg-green-900/25 border border-green-200 dark:border-green-800 p-3 text-sm text-green-700 dark:text-green-300">
                  Done{action.agent_command_id ? ` — sent to the device as command #${action.agent_command_id}` : ""}.
                  {action.result ? (
                    <pre className="mt-2 text-[11px] whitespace-pre-wrap break-all opacity-80">
                      {JSON.stringify(action.result, null, 1)}
                    </pre>
                  ) : null}
                </div>
              )}
              {["approved", "executing", "running"].includes(action.status) && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Running on the device — this updates itself.
                </p>
              )}

              {!settled && (
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {action.risk_tier === "high" ? "Approval note (required)" : "Note (optional)"}
                  </label>
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={
                      action.risk_tier === "high"
                        ? "Why this is being approved"
                        : "Recorded against the action"
                    }
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              )}

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex justify-end gap-2">
          {action && !settled && (
            <>
              <button
                onClick={reject}
                disabled={busy !== null}
                className="text-sm px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:text-gray-100 disabled:opacity-50"
              >
                {busy === "reject" ? "Rejecting…" : "Reject"}
              </button>
              <button
                onClick={run}
                disabled={busy !== null}
                className="text-sm px-4 py-2 rounded-lg bg-brand text-white font-medium disabled:opacity-50"
              >
                {busy === "run" ? "Running…" : "Approve & run"}
              </button>
            </>
          )}
          {(settled || !action) && (
            <button
              onClick={onClose}
              className="text-sm px-4 py-2 rounded-lg bg-brand text-white font-medium"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
